import DataTable from "../../../utils/components/DataTable";
import { MdOutlineEdit } from "react-icons/md";
import { BiPlus } from "react-icons/bi";
import { MdDelete } from "react-icons/md";
import { useContext, useEffect, useState } from "react";
import { useProject } from "../../../utils/hooks/useProject";
import { useOutletContext } from "react-router";
import API from "../../../utils/API";
import { Helmet } from "react-helmet-async";
import Modal from "../../../utils/components/Modal";
import Input from "../../../utils/components/Input";
import Loading from "../../../utils/components/Loading";
import AddNewCompany from "../../Companies/components/AddNewCompany";
import { confirmAlert } from "react-confirm-alert";
import AlertModal from "../../../utils/components/AlertModal";
import { Translate } from "react-localize-redux";
import { RequestContext } from "../../../utils/context/RequestContext";

const CompaniesAndRoles = () => {
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showNewRoleModal, setShowNewRoleModal] = useState(false);
  const [showNewCompanyModal, setShowNewCompanyModal] = useState(false);
  const [roleName, setRoleName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isClickable, setIsClickable] = useState(true);

  const { project } = useProject();

  const [translate] = useOutletContext();

  const { setRequestLoading, requestLoading } = useContext(RequestContext);

  const [columns, setColumns] = useState([
    {
      name: <Translate id="project_settings.companies_and_roles.companies" />,
      // translate("project_settings.companies_and_roles.companies"),
      selector: (row) => row.name,
    },
    {
      name: translate("project_settings.companies_and_roles.company_shortname"),
      selector: (row) => row.short_name,
    },
  ]);

  useEffect(() => {
    if (project?.id) {
      API.getRoles(project.id).then(({ data: res }) => {
        const { data: roles } = res;
        setRoles(roles);

        setTimeout(handleGetCompanyRoles, 20);
      });
    } else setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setColumns([
      {
        name: translate("project_settings.companies_and_roles.companies"),
        selector: (row) => row.name,
        width: "210px",
      },
      {
        name: translate(
          "project_settings.companies_and_roles.company_shortname"
        ),
        selector: (row) => row.short_name,
        width: "180px",
      },
      ...roles.map((role) => ({
        name: (
          <div
            className="d-flex align-items-center"
            style={{ width: "max-content" }}
          >
            <p className="mb-0 pt-1">{role.name}</p>
            <MdOutlineEdit
              className="fs-5 mx-3 cursor--pointer flex-shrink-0"
              onClick={() => {
                setSelectedRole(role);
                project?.id
                  ? setShowNewRoleModal(true)
                  : alert(translate("select_a_project_alert"));
              }}
            />
            <MdDelete
              className="fs-5 cursor--pointer flex-shrink-0"
              onClick={() => onDelete(role.id)}
            />
          </div>
        ),
        cell: (row) => (
          <input
            className="form-check-input ms-5 fs-5"
            type="checkbox"
            checked={row.roles.findIndex((r) => r.roleId === role.id) > -1}
            id={`row-${row.id}-role-${role.id}`}
            onChange={() => handleToggleRoleToCompany(row.id, role.id)}
            disabled={
              isUpdating?.row === row.id && isUpdating?.role === role.id
            }
          />
        ),
      })),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles, companies, isUpdating]);

  useEffect(() => {
    if (selectedRole) {
      setRoleName(selectedRole.name);
    }

    return () => {
      setRoleName("");
    };
  }, [selectedRole]);

  const checkedProjectSelection = () => {
    if (!project?.id) {
      confirmAlert({
        customUI: ({ onClose }) => (
          <AlertModal
            buttons={[
              {
                label: translate("okay"),
                onClick: onClose,
                classNames: "btn btn-primary",
              },
            ]}
            message={translate("select_a_project_alert")}
          />
        ),
      });
      return false;
    }
  };

  const checkedRoleNameTrim = () => {
    if (!roleName.trim()) {
      confirmAlert({
        customUI: ({ onClose }) => (
          <AlertModal
            buttons={[
              {
                label: translate("okay"),
                onClick: onClose,
                classNames: "btn btn-primary",
              },
            ]}
            message={translate("fill_all_fields")}
          />
        ),
      });
      return false;
    }
  };

  const handleEditRole = (e) => {
    e.preventDefault();
    if (requestLoading) return;
    checkedProjectSelection();
    checkedRoleNameTrim();

    setRequestLoading(true);

    API.putData(`roles/${selectedRole.id}`, {
      name: roleName.trim(),
    }).then(({ data: res }) => {
      const newRoles = roles.map((role) =>
        role.id === selectedRole.id ? res.data : role
      );

      setRoles(newRoles);

      setShowNewRoleModal(false);

      setTimeout(() => {
        setRoleName("");
        setSelectedRole(null);
      }, 50);
    });
  };

  const handleCreateRole = (e) => {
    e.preventDefault();

    if (requestLoading) return;

    checkedProjectSelection();
    checkedRoleNameTrim();

    setRequestLoading(true);

    API.postData(`roles?project_id=${project.id}`, {
      name: roleName.trim(),
    }).then(({ data: res }) => {
      setTimeout(() => {
        const { id, name } = res.data;
        setRoles([...roles, { id, name }]);
        setShowNewRoleModal(false);
      }, 20);
      setTimeout(() => {
        setRoleName("");
      }, 50);
    });
  };

  const handleGetCompanyRoles = () => {
    API.getData(`companies/roles?project_id=${project.id}`).then(
      ({ data: res }) => {
        const companies = res.data.map((company) => ({
          id: company.id,
          name: company.name,
          short_name: company.short_name,
          roles: company.roles
            ? company.roles.map((role) => ({
                roleId: role.id,
                companyProjectRoleId: role.company_project_role_id,
              }))
            : [],
        }));

        setCompanies(companies);

        setTimeout(() => {
          setIsLoading(false);
          setIsUpdating(null);
        }, 50);
      }
    );
  };

  const handleAddNewCompany = (company) => {
    setCompanies([
      ...companies,
      {
        id: company.id,
        name: company.name,
        short_name: company.short_name,
        roles: [],
      },
    ]);
  };

  const onDelete = (id) => {
    if (!id) return false;

    confirmAlert({
      customUI: ({ onClose }) => (
        <AlertModal
          buttons={[
            {
              label: translate("cancel"),
              onClick: onClose,
              classNames: "btn btn-secondary",
            },
            {
              label: translate("delete"),
              onClick: () => {
                handleDeleteRole(id, onClose);
              },
              classNames: "btn btn-danger",
            },
          ]}
          message={translate("delete_confirmation")}
        />
      ),
    });
  };
  const handleDeleteRole = (id, closeModal) => {
    if (requestLoading) return;

    setRequestLoading(true);

    API.deleteData(`roles/${id}`).then(() => {
      setRoles(roles.filter((role) => role.id !== id));
      closeModal();
    });
  };

  const handleToggleRoleToCompany = (companyId, roleId) => {
    const company = companies.find((company) => company.id === companyId);
    if (!company) return;

    setIsUpdating({ row: companyId, role: roleId });
    let request = "";

    const roleToRemove = company.roles.find((role) => role.roleId === roleId);

    if (roleToRemove)
      request = API.deleteData(
        `company/role/${roleToRemove.companyProjectRoleId}?project_id=${project.id}`
      );
    else
      request = API.putData(
        `company/${companyId}/role/${roleId}?project_id=${project.id}`
      );

    request.then(() => {
      handleGetCompanyRoles();
    });
  };

  return (
    <div className="StatusSets SetsPage">
      <Helmet>
        <title>
          {translate("page_titles.project-settings-companies-and-roles")} -{" "}
          {translate("page_titles.project-settings")}
        </title>
      </Helmet>
      <div className="row">
        <div className="col-12 d-flex justify-content-start align-items-center">
          <div
            className="d-flex align-items-center mb-4"
            role="button"
            onClick={() => {
              setSelectedRole(null);
              project?.id
                ? setShowNewRoleModal(true)
                : alert(translate("select_a_project_alert"));
            }}
          >
            <BiPlus className="fs-1 bg-primary text-white rounded-circle" />
            <span className="ms-2">
              {translate(
                "project_settings.companies_and_roles.create_new_role_button"
              )}
            </span>
          </div>
          <div
            className="d-flex align-items-center mb-4 ms-3"
            role="button"
            onClick={() =>
              project?.id
                ? setShowNewCompanyModal(true)
                : alert(translate("select_a_project_alert"))
            }
          >
            <BiPlus className="fs-1 bg-primary text-white rounded-circle" />
            <span className="ms-2">
              {translate(
                "project_settings.companies_and_roles.create_new_company_button"
              )}
            </span>
          </div>
        </div>
      </div>
      {project?.id ? (
        <>
          <div className="row">
            <div className="col-12">
              {isLoading ? (
                <Loading />
              ) : (
                <DataTable data={companies} columns={columns} />
              )}
            </div>
          </div>

          <Modal
            title={translate(
              `project_settings.companies_and_roles.${
                selectedRole ? "edit-role" : "create_new_role"
              }`
            )}
            positiveButtonNode={translate("save")}
            negativeButtonNode={translate("cancel")}
            positiveButtonHandler={
              selectedRole ? handleEditRole : handleCreateRole
            }
            modalSize="lg"
            show={showNewRoleModal}
            setShow={(show) => {
              setShowNewRoleModal(show);
            }}
          >
            <form onSubmit={selectedRole ? handleEditRole : handleCreateRole}>
              <div className="form-group">
                <Input
                  type="text"
                  name="roleName"
                  title={translate(
                    "project_settings.companies_and_roles.role_name"
                  )}
                  placeholder={translate(
                    "project_settings.companies_and_roles.role_name_placeholder"
                  )}
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                />
              </div>
            </form>
          </Modal>

          {project?.id && (
            <AddNewCompany
              show={showNewCompanyModal}
              setShow={setShowNewCompanyModal}
              translate={translate}
              refreshCompanies={(company) => handleAddNewCompany(company)}
              isClickable={isClickable}
              setIsClickable={setIsClickable}
            />
          )}
        </>
      ) : (
        ``
      )}
    </div>
  );
};
export default CompaniesAndRoles;
